import {
  ClipboardCheck,
  Copy,
  Headphones,
  Lightbulb,
  RefreshCcw
} from "lucide-react"
import { FC, useState } from "react"
import { Integration } from "../../../../types"
import { UserDetails } from "../../../../types/user"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { PrimaryBtn } from "../../buttons"
import { useAppSelector } from "../../../hooks"
import { BB_DETAILS } from "../../../constants"
import { EMBED_DOCUMENTATION_LINK } from "../../../constants/global"
import { buildSupportMailto } from "../helpers"

const STEPS = [
  {
    label: "Copy the code snippet from the code block above to your clipboard.",
    icon: Copy
  },
  {
    label: (
      <>
        Paste the code directly into a{" "}
        <code className="font-mono text-xs bg-violet-50 text-primary-600 px-1.5 py-0.5 rounded">
          &lt;HTML&gt;
        </code>{" "}
        section of your webpage codebase. Publish changes.
      </>
    ),
    icon: ClipboardCheck
  },
  {
    label:
      "Reload your webpage to verify the integration is displaying correctly.",
    icon: RefreshCcw
  }
]

interface Props {
  integration?: Integration
}

const Install: FC<Props> = ({ integration }) => {
  const [copied, setCopied] = useState(false)
  const user = useAppSelector<UserDetails | undefined>(selectUserDetails)

  const snippet = integration?.app_settings?.install_script ?? ""
  const supportHref = integration
    ? buildSupportMailto(
        integration,
        user?.username || user?.login || "",
        user?.email || ""
      )
    : `mailto:${BB_DETAILS.email}`

  const copy = () => {
    navigator.clipboard.writeText(snippet).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="flex flex-col gap-7">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2.5 tracking-tight">
          Install Integration
        </h2>
        <p className="text-sm text-neutral-500 leading-relaxed">
          Follow these steps to deploy the BankingBridge code snippet into your
          website environment.
        </p>
      </div>

      {/* Code snippet panel */}
      <div className="bg-[#ECEEF2] rounded-lg p-4">
        <div className="flex items-center justify-between mb-3.5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#63C7C4] inline-block" />
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Code Snippet for Install
            </span>
          </div>
          <PrimaryBtn onClick={copy}>
            {copied ? (
              <ClipboardCheck size={13} className="mr-1.5" />
            ) : (
              <Copy size={13} className="mr-1.5" />
            )}
            {copied ? "Copied!" : "Copy Snippet"}
          </PrimaryBtn>
        </div>
        <pre className="bg-white rounded-md px-6 py-5 font-mono text-sm leading-7 text-neutral-700 overflow-x-auto whitespace-pre-wrap">
          {snippet || (
            <span className="text-neutral-400 italic">
              No install script available.
            </span>
          )}
        </pre>
      </div>

      {/* Steps + Guide */}
      <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1fr_360px] items-start">
        {/* Steps */}
        <div>
          <h3 className="text-base font-bold text-neutral-900 mb-5">
            Steps to Install
          </h3>
          <ol className="flex flex-col gap-3.5">
            {STEPS.map((step, i) => (
              <li key={i} className="flex gap-3.5 items-start">
                <span className="w-5 h-5 rounded-full border border-neutral-300 inline-flex items-center justify-center text-xs font-medium text-neutral-500 shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-neutral-700 leading-relaxed">
                  {step.label}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* Guide */}
        <div className="bg-[#ECEEF2] rounded-lg p-5">
          <h3 className="text-base font-bold text-neutral-900 mb-3.5">
            Installation Guide &amp; Tips
          </h3>
          <div className="bg-white/55 rounded-md p-3.5 relative pl-11">
            <Lightbulb
              size={20}
              className="text-primary-500 absolute left-3.5 top-3.5"
            />
            <p className="text-sm text-neutral-700 leading-relaxed mb-2.5">
              Ensure the script is loaded <strong>asynchronously</strong> to
              maintain optimal page performance and SEO rankings.
            </p>
            <a
              href={EMBED_DOCUMENTATION_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary-600 hover:text-primary-700"
            >
              View Installation Documentation
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Support footer */}
      <div className="bg-[#ECEEF2] rounded-lg px-5 py-4 flex items-center gap-3.5">
        <span className="w-10 h-10 rounded-full bg-white inline-flex items-center justify-center shrink-0">
          <Headphones size={20} className="text-primary-500" />
        </span>
        <div>
          <div className="text-sm font-bold text-neutral-900">
            Need support assistance?
          </div>
          <div className="text-sm text-neutral-500 mt-0.5">
            Reach out to{" "}
            <a href={supportHref} className="text-primary-600 font-medium">
              {BB_DETAILS.email}
            </a>{" "}
            for troubleshooting.
          </div>
        </div>
      </div>
    </div>
  )
}

export default Install
